import {Component, EventEmitter, Input, OnInit, Output, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, NgZone, ViewChild, OnChanges, SimpleChanges, ElementRef} from '@angular/core';
import {FormGroup, FormControl, FormBuilder, Validators} from "@angular/forms";
import {Subscription, BehaviorSubject, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {AccordionPanelComponent} from 'ngx-bootstrap/accordion';

@Component({
  selector: 'app-dispute-box',
  templateUrl: './dispute-box.component.html',
  styleUrls: ['./dispute-box.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DisputeBoxComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  @ViewChild('Group', { static: false }) accordionGroup!: AccordionPanelComponent;
  @Input() dynamicForm: FormGroup = new FormGroup({});
  @Output() dynamicFormValid = new EventEmitter<boolean>();
  @Output() disputeBoxValid = new EventEmitter<boolean>();
  @Output() disputeBoxValidated = new EventEmitter<void>();
  @Input() first = false;
  @Input() isOpen = false;
  @Input() index: number = 0;
  @Input() valueInDispute: any;
  @Output() removeItem = new EventEmitter();
  @Input() fields: any;
  @Input() projectShortName: string = '';
  @Input() tokenIdentifier: string = '';
  @Input() sessionToken: string = '';
  @Input() isDispute: boolean = false;
  @Input() projectSettings: any = {};
  @Input() isDisputeForm: boolean = false;
  @Input() enableMultipleProductsSelection: boolean = false;
  @Input() productSelection: any[] = [];
  @Input() existingFiles: any[] = [];
  @Input() disableProductSelection: boolean = false;
  @Input() disableFileGet: boolean = false;
  @Input() isDatagateMode = false;
  @Input() title: string = 'Anspruch';
  @Input() shouldCollapse: boolean = false;
  @Input() hideDeleteIcon: boolean = false;
  @Output() openChange = new EventEmitter<boolean>();

  private isValidSubject = new BehaviorSubject<boolean>(false);
  public isValid$ = this.isValidSubject.asObservable();
  public selectedProductFields: any = null;
  public selectedProductName: string | null = null;
  private formSubscription: Subscription = new Subscription();
  private productSubscription: Subscription = new Subscription();
  private fileValidationStates: Map<string, boolean> = new Map();
  private ngUnsubscribe = new Subject<void>();
  private isFormControlsInitialized = false;
  private formControlCache = new Map<string, FormControl>();
  private lastFieldsHash = '';

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  // Custom validator for boolean fields that accepts "0" and "1" as valid values
  booleanRequiredValidator(control: any) {
    if (control.value === null || control.value === undefined || control.value === '') {
      return { required: true };
    }
    // Accept "0" (Nein) and "1" (Ja) as valid values
    if (control.value === '0' || control.value === '1') {
      return null; // Valid
    }
    return null; // Valid for any other value
  }

  ngOnInit() {
    // Only initialize form controls once
    if (!this.isFormControlsInitialized) {
      this.ensureAllFormControlsExist();
      this.updateExistingBooleanValidators();
      this.isFormControlsInitialized = true;
    }

    this.setupFormValueMonitoring();

    // Debug the condition that determines if we should set up default product
    const shouldSetupDefaultProduct = !this.enableMultipleProductsSelection && this.productSelection?.length > 0;
    const shouldUseFieldsInput = !this.enableMultipleProductsSelection && this.fields?.SelectedFields?.length > 0;

    // Initialize the form if it doesn't exist
    if (!this.dynamicForm) {
      this.dynamicForm = this.fb.group({});
    }

    // Create the Product control if it doesn't exist
    const productControlName = `Product_${this.valueInDispute.id}`;
    if (!this.dynamicForm.contains(productControlName)) {
      this.dynamicForm.addControl(productControlName, this.fb.control(''));
    }

    // Initialize file upload controls if needed
    if (this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
      this.projectSettings.streitwertRequiredFileCategories.forEach((category: any) => {
        const fileControlName = `Files_${category.id}_${this.valueInDispute.id}`;
        if (!this.dynamicForm.contains(fileControlName)) {
          this.dynamicForm.addControl(fileControlName, this.fb.control(''));
        }
      });
    }

    // Set up fields based on mode
    if (!this.enableMultipleProductsSelection && this.fields?.SelectedFields?.length > 0) {
      // Single product mode - fields come from the fields input
      this.initializeSingleProductFields();

    } else if (shouldSetupDefaultProduct) {
      // Multiple product mode with default product
      const defaultProduct = this.productSelection[0];

      this.selectedProductFields = {
        SelectedFields: defaultProduct.selectedFields.map((field: any) => ({
          Type: field.type,
          FieldName: field.fieldName,
          Name: `${field.fieldName}_${this.valueInDispute.id}`,
          SpaLabel: field.spaLabel,
          Label: field.label || field.spaLabel,
          Required: field.required || field.mandatory,
          Mandatory: field.required || field.mandatory,
          Options: field.options || [],
          Min: field.min,
          Max: field.max,
          MaxLength: typeof field.maxLength === 'string' ? parseInt(field.maxLength, 10) : field.maxLength,
          Multiselect: field.multiselect || false,
          Order: field.order || 0,
          AvailableForUpdateIfHasValue: field.availableForUpdateIfHasValue || false,
          DisplayForExistingAkt: field.displayForExistingAkt || false,
          DisplayForExistingStreitwert: field.displayForExistingStreitwert || false,
          phoneNumber: field.phoneNumber || false,
          email: field.email || false,
          url: field.url || false,
          Currency: field.currency || []
        }))
      };
      this.selectedProductName = defaultProduct.streitwertProduct.name;
    }

    // Initialize form controls for each field - only if not already done
    if (this.selectedProductFields?.SelectedFields && !this.enableMultipleProductsSelection) {
      this.selectedProductFields.SelectedFields.forEach((field: any) => {
        const controlName = `${field.FieldName}_${this.valueInDispute.id}`;

        // Only add control if it doesn't already exist
        if (!this.dynamicForm.contains(controlName)) {
          const isRequired = field.Required || field.Mandatory;
          const control = this.fb.control('', isRequired ? [Validators.required] : []);
          this.dynamicForm.addControl(controlName, control);
          this.formControlCache.set(controlName, control);
        } else {
          // Still cache the existing control
          const existingControl = this.dynamicForm.get(controlName) as FormControl;
          this.formControlCache.set(controlName, existingControl);
        }
      });
    }

    // Initialize file controls for streitwert file categories
    if (this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
      this.projectSettings.streitwertRequiredFileCategories.forEach((category: any) => {
        const fileControlName = `Files_${category.id}_${this.valueInDispute.id}`;

        // Only add control if it doesn't already exist
        if (!this.dynamicForm.contains(fileControlName)) {
          // File controls don't need validators as validation is handled by the file component
          const control = this.fb.control(null);
          this.dynamicForm.addControl(fileControlName, control);
        }
      });
    }

    // Subscribe to form value changes with more comprehensive validation
    this.formSubscription = this.dynamicForm.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(100) // Small debounce to avoid excessive validation calls
      )
      .subscribe(() => {
        this.updateValidationState();
      });

    // Also subscribe to form status changes
    this.dynamicForm.statusChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.updateValidationState();
      });

    // Subscribe to individual field changes for required fields
    if (this.selectedProductFields?.SelectedFields) {
      this.selectedProductFields.SelectedFields.forEach((field: any) => {
        if (field.Required || field.Mandatory) {
          const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
          const control = this.dynamicForm.get(controlName);
          if (control) {
            control.valueChanges
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(() => {
                this.updateValidationState();
              });
          }
        }
      });
    }

    // Subscribe to product selection changes
    if (this.enableMultipleProductsSelection) {
      const productControl = this.getFormControlSafe('Product');
      if (productControl) {
        this.productSubscription = productControl.valueChanges
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((selectedProductId) => {
            this.ngZone.run(() => {
              if (selectedProductId) {
                // Find the selected product and its fields
                const selectedProduct = this.productSelection.find(
                  item => item.streitwertProduct.id === selectedProductId
                );

                if (selectedProduct) {
                  // Store current form values before changing fields
                  const currentFormValues = new Map<string, any>();
                  if (this.selectedProductFields?.SelectedFields) {
                    this.selectedProductFields.SelectedFields.forEach((field: any) => {
                      const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
                      const control = this.dynamicForm.get(controlName);
                      if (control && control.value !== '' && control.value !== null) {
                        currentFormValues.set(field.FieldName, control.value);
                      }
                    });
                  }

                  // Remove only controls that are no longer needed
                  this.removeUnusedFieldControls(selectedProduct.selectedFields);

                  this.selectedProductFields = {
                    SelectedFields: selectedProduct.selectedFields.map((field: any) => ({
                      Type: field.type,
                      FieldName: field.fieldName,
                      Name: `${field.fieldName}_${this.valueInDispute.id}`,
                      SpaLabel: field.spaLabel,
                      Label: field.label || field.spaLabel,
                      Required: field.required || field.mandatory,
                      Mandatory: field.required || field.mandatory,
                      Options: field.options || [],
                      Min: field.min,
                      Max: field.max,
                      MaxLength: typeof field.maxLength === 'string' ? parseInt(field.maxLength, 10) : field.maxLength,
                      Multiselect: field.multiselect || false,
                      Order: field.order || 0,
                      AvailableForUpdateIfHasValue: field.availableForUpdateIfHasValue || false,
                      DisplayForExistingAkt: field.displayForExistingAkt || false,
                      DisplayForExistingStreitwert: field.displayForExistingStreitwert || false,
                      valueToDisplay: field.valueToDisplay,
                      phoneNumber: field.phoneNumber || false,
                      email: field.email || false,
                      url: field.url || false,
                      Currency: field.currency || []
                    }))
                  };
                  if(!this.isDatagateMode) {
                    this.selectedProductName = selectedProduct.streitwertProduct.name;
                  }
                  // Initialize form controls for the selected product, preserving existing values
                  this.selectedProductFields.SelectedFields.forEach((field: any) => {
                    const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
                    const isRequired = field.Required || field.Mandatory;

                    // Check if control already exists
                    if (!this.dynamicForm.contains(controlName)) {
                      // Create new control with preserved value if available
                      const preservedValue = currentFormValues.get(field.FieldName) || '';
                      
                      // Create validators based on field type
                      let validators: any[] = [];
                      if (isRequired) {
                        if (field.Type === 'boolean') {
                          // Custom validator for boolean fields that accepts "0" and "1" as valid
                          validators = [this.booleanRequiredValidator];
                        } else {
                          validators = [Validators.required];
                        }
                      }
                      
                      const control = this.fb.control(preservedValue, validators);
                      this.dynamicForm.addControl(controlName, control);
                      this.formControlCache.set(controlName, control);

                      // Subscribe to changes for required fields
                      if (isRequired) {
                        control.valueChanges
                          .pipe(takeUntil(this.ngUnsubscribe))
                          .subscribe(() => {
                            this.updateValidationState();
                          });
                      }
                    } else {
                      // Control already exists, just cache it and ensure it has the right validators
                      const existingControl = this.dynamicForm.get(controlName) as FormControl;
                      if (isRequired) {
                        let validators: any[] = [];
                        if (field.Type === 'boolean') {
                          validators = [this.booleanRequiredValidator];
                        } else {
                          validators = [Validators.required];
                        }
                        
                        // Check if the control needs validator updates
                        const needsUpdate = !existingControl.hasValidator(validators[0]);
                        if (needsUpdate) {
                          existingControl.setValidators(validators);
                          existingControl.updateValueAndValidity();
                        }
                      }
                      this.formControlCache.set(controlName, existingControl);
                    }
                  });

                  // Initialize file controls for the new product
                  if (this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
                    this.projectSettings.streitwertRequiredFileCategories.forEach((category: any) => {
                      const fileControlName = `Files_${category.id}_${this.valueInDispute.id}`;
                      if (!this.dynamicForm.contains(fileControlName)) {
                        const control = this.fb.control(null);
                        this.dynamicForm.addControl(fileControlName, control);
                      }
                    });
                  }

                  // Reset and re-validate files for the new product
                  this.resetAndRevalidateFiles();

                  // Force template re-rendering
                  this.forceComponentUpdate();

                  // Force complete validation reset
                  setTimeout(() => {
                    this.forceValidationReset();
                    this.debugFormState();
                    this.verifyFieldMapping();
                  }, 300);
                }
              } else {
                this.removeExistingFieldControls();
                this.selectedProductFields = null;
                this.selectedProductName = null;
                this.resetAndRevalidateFiles();
                this.forceComponentUpdate();

                // Force complete validation reset
                setTimeout(() => {
                  this.forceValidationReset();
                }, 300);
              }
              // Force immediate revalidation after product change
              setTimeout(() => {
                this.updateValidationState();
                this.forceComponentUpdate();
              }, 50);
            });
          });
      }
    }

    // Initial validation state - delay to ensure all components are ready
    setTimeout(() => {
      this.updateValidationState();
    }, 100);
  }

  ngAfterViewInit(): void {
    // Initial validation check after view is initialized
    setTimeout(() => {
      this.updateValidationState();
      this.cdr.detectChanges();
      this.cdr.markForCheck();
    }, 100); // Increased delay to ensure form controls are ready
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.productSubscription) {
      this.productSubscription.unsubscribe();
    }
    // Clean up form controls when component is destroyed
    this.removeExistingFieldControls();
  }

  getFormControl(fieldName: string): FormControl {
    const controlName = `${fieldName}_${this.valueInDispute.id}`;
    let control = this.dynamicForm.get(controlName) as FormControl;

    if (!control) {
      control = this.fb.control('');
      this.dynamicForm.addControl(controlName, control);
    }

    return control;
  }

  // Method to get form control without creating it dynamically (for template use)
  getFormControlSafe(fieldName: string): FormControl {
    const controlName = `${fieldName}_${this.valueInDispute.id}`;

    // Check cache first
    if (this.formControlCache.has(controlName)) {
      const cachedControl = this.formControlCache.get(controlName)!;
      return cachedControl;
    }

    const control = this.dynamicForm.get(controlName) as FormControl;

    // If control doesn't exist, return a dummy control to prevent errors
    // The actual control should be created during initialization
    if (!control) {
      return this.fb.control('');
    }

    // Cache the control for future use
    this.formControlCache.set(controlName, control);

    return control;
  }

  // Method to get file form control without creating it dynamically (for template use)
  getFileFormControlSafe(categoryId: string): FormControl {
    const controlName = `Files_${categoryId}_${this.valueInDispute.id}`;
    const control = this.dynamicForm.get(controlName) as FormControl;

    // If control doesn't exist, return a dummy control to prevent errors
    // The actual control should be created during initialization
    if (!control) {
      return this.fb.control('');
    }

    return control;
  }

  isDateValid(fieldName: string): boolean {
    const control = this.getFormControlSafe(fieldName);
    return control.valid && control.value && control.touched;
  }

  isDateInvalid(fieldName: string): boolean {
    const control = this.getFormControlSafe(fieldName);
    return control.invalid && control.touched;
  }

  removeDisputeinValue(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.removeExistingFieldControls();
    this.removeItem.emit(this.index);
  }

  private checkFilesValidity(): boolean {
    if (!this.projectSettings?.streitwertRequiredFileCategories?.length) {
      return true;
    }

    // Check each required file category
    return this.projectSettings.streitwertRequiredFileCategories.every((category: any) => {

      // If minimumFiles is 0, the category is optional
      if (category.minimumFiles === 0) {
        return true;
      }

      const fileControlName = `Files_${category.id}_${this.valueInDispute.id}`;
      const fileControl = this.dynamicForm.get(fileControlName);

      if (!fileControl) {
        return false;
      }

      const fileValue = fileControl.value;
      let totalFiles = 0;

      // Check uploaded files from the form control
      if (fileValue && typeof fileValue === 'object') {
        const uploadedFiles = fileValue.files || (fileValue as any).uploadedFiles || [];


        // Only count files that are marked as claim files
        const claimFiles = uploadedFiles.filter((file: any) => file.isClaimFile === true);

        totalFiles += claimFiles.length;
      }

      // Check existing files from the API
      if (this.existingFiles?.length > 0) {
        const existingFiles = this.existingFiles.filter((file: any) => {
          const isCorrectCategory = file.fileCategoryId === category.id;
          const isValueInDispute = file.valueInDispute === true;
          const isClaimFile = file.isClaimFile === true;
          const isForThisSection = file.sectionId === this.valueInDispute.id;
          return isCorrectCategory && isValueInDispute && isClaimFile && isForThisSection;
        });

        totalFiles += existingFiles.length;
      }

      return totalFiles >= category.minimumFiles;
    });
  }

  private areFilesValid(): boolean {
    if (!this.projectSettings?.streitwertRequiredFileCategories?.length) {
      return true;
    }

    // If we have validation states from file upload components, use those
    if (this.fileValidationStates.size > 0) {
      // Check if all file categories are either optional or have valid states
      const allCategoriesValid = this.projectSettings.streitwertRequiredFileCategories.every((category: any) => {
        // Optional categories are always valid
        if (category.minimumFiles === 0) {
          return true;
        }

        // Check if we have a validation state for this category
        const validationState = this.fileValidationStates.get(category.id);

        // If we have a validation state, use it
        if (validationState !== undefined) {
          return validationState;
        }

        // If no validation state yet, assume invalid until file component validates
        // This prevents premature validation before file upload components are ready
        return false;
      });

      return allCategoriesValid;
    }

    // Fallback to original logic if no validation states
    const result = this.projectSettings.streitwertRequiredFileCategories.every((category: any) => {
      // If minimumFiles is 0, the category is optional
      if (category.minimumFiles === 0) {
        return true;
      }

      const fileControlName = `Files_${category.id}_${this.valueInDispute.id}`;
      const fileControl = this.dynamicForm.get(fileControlName);

      if (!fileControl) {
        return false;
      }

      // Mark control as touched to trigger validation
      if (!fileControl.touched) {
        fileControl.markAsTouched();
      }

      const fileValue = fileControl.value;
      let totalFiles = 0;

      // Check uploaded files from the form control
      if (fileValue && typeof fileValue === 'object') {
        const uploadedFiles = fileValue.files || (fileValue as any).uploadedFiles || [];
        // Only count files that are marked as claim files
        const claimFiles = uploadedFiles.filter((file: any) => file.isClaimFile === true);
        totalFiles += claimFiles.length;
      }

      // Check existing files from the API
      if (this.existingFiles?.length > 0) {
        const existingFiles = this.existingFiles.filter((file: any) => {
          const isCorrectCategory = file.fileCategoryId === category.id;
          const isValueInDispute = file.valueInDispute === true;
          const isClaimFile = file.isClaimFile === true;
          const isForThisSection = file.sectionId === this.valueInDispute.id;
          return isCorrectCategory && isValueInDispute && isClaimFile && isForThisSection;
        });

        totalFiles += existingFiles.length;
      }

      const isValid = totalFiles >= category.minimumFiles;

      return isValid;
    });

    return result;
  }

  private updateValidationState(): void {
    const formValid = this.isFormValid();
    const filesValid = this.areFilesValid();
    const overallValid = formValid && filesValid;


    // Update internal state
    this.isValidSubject.next(overallValid);

    // Emit validation state to parent - use index for proper tracking
    this.disputeBoxValid.emit(overallValid);

    // Also emit through dynamicFormValid for backward compatibility
    this.dynamicFormValid.emit(overallValid);


    // Force change detection in the next tick
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 0);
  }

  private isFormValid(): boolean {
    if (!this.dynamicForm) {
      return false;
    }

    // Check if product is selected when multiple products are enabled
    if (this.enableMultipleProductsSelection) {
      const productControl = this.getFormControlSafe('Product');
      if (!productControl?.value) {
        return false;
      }
    }

    // If no selected product fields, the form is valid (all fields might be hidden)
    if (!this.selectedProductFields?.SelectedFields || this.selectedProductFields.SelectedFields.length === 0) {
      return true;
    }

    // Check all form controls
    let isValid = true;
    let hasRequiredFields = false;
    let requiredFieldResults: any[] = [];

    this.selectedProductFields.SelectedFields.forEach((field: any) => {
      const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
      const control = this.dynamicForm.get(controlName);

      if (control) {
        // Check if control is valid and has a value
        if (field.Required || field.Mandatory) {
          hasRequiredFields = true;
          const hasValue = control.value !== null && control.value !== undefined && control.value !== '';
          const isFieldValid = control.valid && hasValue;
          isValid = isValid && isFieldValid;

          const fieldResult = {
            fieldName: field.FieldName,
            required: field.Required,
            mandatory: field.Mandatory,
            value: control.value,
            valid: control.valid,
            hasValue: hasValue,
            isFieldValid: isFieldValid,
            controlName: controlName
          };

          requiredFieldResults.push(fieldResult);
        }
      } else {
        if (field.Required || field.Mandatory) {
          isValid = false;
          requiredFieldResults.push({
            fieldName: field.FieldName,
            required: field.Required,
            mandatory: field.Mandatory,
            error: 'Control not found',
            isFieldValid: false,
            controlName: controlName
          });
        }
      }
    });

    // If there are no required fields to validate, the form is valid
    if (!hasRequiredFields) {
      return true;
    }

    return isValid;
  }

  onFileValidationChange(isValid: boolean, categoryId: string) {
    // Store the validation state for this category
    this.fileValidationStates.set(categoryId, isValid);

    // Immediate revalidation when file validation changes
    setTimeout(() => {
      this.updateValidationState();
    }, 0);
  }

  onFileUpload(event: any): void {
    if (event) {
      event.stopPropagation();
    }



    // Immediate revalidation when files are uploaded
    setTimeout(() => {
      this.updateValidationState();
    }, 100);
  }

  onProgressChanged(): void {
    // Trigger change detection when upload progress changes
    this.cdr.markForCheck();
  }

  // Add a method to manually trigger revalidation
  public triggerRevalidation(): void {
    this.updateValidationState();
  }

  // Method to manually close the accordion
  public closeAccordion(): void {
    if (this.accordionGroup) {
      this.accordionGroup.isOpen = false;
      this.cdr.markForCheck();
    }
  }

  // Method to collapse accordion when valid with retry logic
  private collapseAccordionWhenValid(): void {
    const attemptCollapse = (retryCount = 0) => {
      if (this.accordionGroup) {
        console.log('Collapsing accordion, isOpen before:', this.accordionGroup.isOpen);
        this.accordionGroup.isOpen = false;
        this.cdr.markForCheck();
        console.log('Accordion collapsed, isOpen after:', this.accordionGroup.isOpen);
      } else if (retryCount < 10) {
        // Retry up to 10 times with 50ms intervals
        console.log(`Accordion group not available, retrying... (${retryCount + 1}/10)`);
        setTimeout(() => attemptCollapse(retryCount + 1), 50);
      } else {
        console.warn('Accordion group not available after 10 retries');
        // Fallback: try to find the accordion element directly
        this.collapseAccordionFallback();
      }
    };

    // Start with a small delay to ensure the accordion is ready
    setTimeout(() => attemptCollapse(), 100);
  }

  // Fallback method to collapse accordion using DOM manipulation
  private collapseAccordionFallback(): void {
    console.log('Using fallback method to collapse accordion');
    const accordionElement = document.querySelector(`[data-section-id="${this.valueInDispute.id}"] .panel-collapse`) as HTMLElement;
    if (accordionElement) {
      accordionElement.classList.remove('in', 'show');
      accordionElement.style.display = 'none';
      accordionElement.setAttribute('aria-hidden', 'true');
      
      const panel = accordionElement.closest('.panel');
      if (panel) {
        const button = panel.querySelector('.accordion-toggle');
        if (button) {
          button.setAttribute('aria-expanded', 'false');
        }
        
        const chevron = panel.querySelector('.fa-chevron-up, .fa-chevron-down');
        if (chevron) {
          chevron.className = chevron.className.replace('fa-chevron-up', 'fa-chevron-down');
        }
      }
      
      console.log('Accordion collapsed using fallback method');
    }
  }

  // Handle accordion toggle events
  public onAccordionToggle(isOpen: boolean): void {
    this.isOpen = isOpen;
    this.openChange.emit(isOpen);
  }

  // Called when user clicks "OK & schließen" – close accordion and notify parent
  public onOkAndClose(): void {
    this.closeAccordion();
    this.openChange.emit(false);
  }

  // Add a method to force template re-rendering
  public forceTemplateUpdate(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  // Method to force component update when product changes
  private forceComponentUpdate(): void {
    this.cdr.markForCheck();
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
  }

  // Method to reset and re-validate files after product change
  private resetAndRevalidateFiles(): void {
    // Clear file validation states
    this.fileValidationStates.clear();

    // Re-check file validation after a short delay
    setTimeout(() => {
      this.updateValidationState();
    }, 150);
  }

  // Method to force complete validation reset and update
  public forceValidationReset(): void {
    // Clear all validation states
    this.fileValidationStates.clear();

    // Force form validation check
    if (this.dynamicForm) {
      this.dynamicForm.updateValueAndValidity();
    }

    // Update validation state after a delay
    setTimeout(() => {
      this.updateValidationState();
    }, 200);
  }

  // Method to debug current form state
  public debugFormState(): void {
    // Debug method - console.log statements removed
  }

  // Method to verify field mapping and control creation
  private verifyFieldMapping(): void {
    // Field mapping verification - console.log statements removed
  }

  ngOnChanges(changes: SimpleChanges) {
    // Check if any inputs that affect form controls have changed
    if (changes['productSelection'] || changes['fields']) {
      // Only create form controls if they haven't been initialized yet
      // This prevents recreation during user input
      if (!this.isFormControlsInitialized) {
        this.ensureAllFormControlsExist();
        this.isFormControlsInitialized = true;
      } else {
        // Only react to meaningful changes in fields
        let shouldReactToFieldsChange = false;
        if (changes['fields']) {
          shouldReactToFieldsChange = this.hasFieldsMeaningfullyChanged(this.fields);
        }

        if (shouldReactToFieldsChange) {
          this.ensureAllFormControlsExist();
        }
      }
    }

    // Handle fields changes for single product mode
    if (changes['fields'] && !this.enableMultipleProductsSelection && this.fields?.SelectedFields?.length > 0) {
      // Don't recreate controls here - they should already exist from initialization
    }

    // Handle shouldCollapse input
    if (changes['shouldCollapse'] && this.shouldCollapse) {
      setTimeout(() => {
        this.closeAccordion();
      }, 100);
    }
  }

  private initializeSingleProductFields(): void {
    if (!this.fields?.SelectedFields) return;

    this.selectedProductFields = {
      SelectedFields: this.fields.SelectedFields.map((field: any) => ({
        Type: field.Type,
        FieldName: field.FieldName,
        Name: `${field.FieldName}_${this.valueInDispute.id}`,
        SpaLabel: field.SpaLabel,
        Label: field.Label || field.SpaLabel,
        Required: field.Required || field.Mandatory,
        Mandatory: field.Required || field.Mandatory,
        Options: field.Options || [],
        Min: field.Min,
        Max: field.Max,
        MaxLength: field.MaxLength,
        Multiselect: field.Multiselect || false,
        Order: field.Order || 0,
        AvailableForUpdateIfHasValue: field.AvailableForUpdateIfHasValue || false,
        DisplayForExistingAkt: field.DisplayForExistingAkt || false,
        DisplayForExistingStreitwert: field.DisplayForExistingStreitwert || false,
        phoneNumber: field.phoneNumber || false,
        email: field.email || false,
        url: field.url || false,
        Currency: field.Currency || [],
        valueToDisplay: field.valueToDisplay
      }))
    };
    this.selectedProductName = 'Streitwert';

    // Initialize form controls for each field
    this.selectedProductFields.SelectedFields.forEach((field: any) => {
      const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
      const isRequired = field.Required || field.Mandatory;

      // Determine initial value
      let initialValue = '';
      if (field.valueToDisplay) {
        if (['lookup', 'optionset', 'boolean'].includes(field.Type) && field.Options) {
          const matchingOption = field.Options.find((opt: any) => opt.option === field.valueToDisplay);
          initialValue = matchingOption ? matchingOption.value : '';
        } else {
          initialValue = field.valueToDisplay;
        }
      }

      if (!this.dynamicForm.contains(controlName)) {
        let validators: any[] = [];
        if (isRequired) {
          if (field.Type === 'boolean') {
            validators = [this.booleanRequiredValidator];
          } else {
            validators = [Validators.required];
          }
        }
        const control = this.fb.control(initialValue, validators);
        this.dynamicForm.addControl(controlName, control);
        this.formControlCache.set(controlName, control);
      } else {
        // Still cache the existing control
        const existingControl = this.dynamicForm.get(controlName) as FormControl;
        this.formControlCache.set(controlName, existingControl);
        // Set the value if initialValue is not empty and different from current
        if (initialValue !== '' && existingControl.value !== initialValue) {
          existingControl.setValue(initialValue, { emitEvent: false });
        }
      }
    });
  }

  private updateExistingBooleanValidators(): void {
    // Update existing form controls for boolean fields to use custom validator
    if (this.productSelection?.length > 0) {
      this.productSelection.forEach((product: any) => {
        if (product.selectedFields?.length > 0) {
          product.selectedFields.forEach((field: any) => {
            if (field.type === 'boolean') {
              const controlName = `${field.fieldName}_${this.valueInDispute.id}`;
              const control = this.dynamicForm.get(controlName) as FormControl;
              if (control && (field.required || field.mandatory)) {
                // Always apply custom validator for boolean fields if they don't already have it
                if (!control.hasValidator(this.booleanRequiredValidator)) {
                  control.setValidators([this.booleanRequiredValidator]);
                  control.updateValueAndValidity();
                }
              }
            }
          });
        }
      });
    }

    // Also update for legacy fields
    if (this.fields?.SelectedFields?.length > 0) {
      this.fields.SelectedFields.forEach((field: any) => {
        if (field.Type === 'boolean') {
          const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
          const control = this.dynamicForm.get(controlName) as FormControl;
          if (control && (field.Required || field.Mandatory)) {
            // Always apply custom validator for boolean fields if they don't already have it
            if (!control.hasValidator(this.booleanRequiredValidator)) {
              control.setValidators([this.booleanRequiredValidator]);
              control.updateValueAndValidity();
            }
          }
        }
      });
    }
  }

  private ensureAllFormControlsExist(): void {
    // Create form controls for all possible fields from all products to prevent cursor disappearing
    if (this.productSelection?.length > 0) {
      this.productSelection.forEach((product: any, index: number) => {
        if (product.selectedFields?.length > 0) {
          product.selectedFields.forEach((field: any) => {
            const controlName = `${field.fieldName}_${this.valueInDispute.id}`;
            if (!this.dynamicForm.contains(controlName)) {
              const isRequired = field.required || field.mandatory;
              let validators: any[] = [];
              if (isRequired) {
                if (field.type === 'boolean') {
                  validators = [this.booleanRequiredValidator];
                } else {
                  validators = [Validators.required];
                }
              }
              const control = this.fb.control('', validators);
              this.dynamicForm.addControl(controlName, control);
              this.formControlCache.set(controlName, control);
            } else {
              // Still cache the existing control
              const existingControl = this.dynamicForm.get(controlName) as FormControl;
              this.formControlCache.set(controlName, existingControl);
            }
          });
        }
      });
    }

    // Also create controls for fields from the fields input if available
    if (this.fields?.SelectedFields?.length > 0) {
      this.fields.SelectedFields.forEach((field: any) => {
        const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
        if (!this.dynamicForm.contains(controlName)) {
          const isRequired = field.Required || field.Mandatory;
          let validators: any[] = [];
          if (isRequired) {
            if (field.Type === 'boolean') {
              validators = [this.booleanRequiredValidator];
            } else {
              validators = [Validators.required];
            }
          }
          const control = this.fb.control('', validators);
          this.dynamicForm.addControl(controlName, control);
          this.formControlCache.set(controlName, control);
        } else {
          // Still cache the existing control
          const existingControl = this.dynamicForm.get(controlName) as FormControl;
          this.formControlCache.set(controlName, existingControl);
        }
      });
    }
  }

  private setupFormValueMonitoring(): void {
    // Monitor all form control value changes
    Object.keys(this.dynamicForm.controls).forEach(controlName => {
      const control = this.dynamicForm.get(controlName) as FormControl;
      if (control) {
        control.valueChanges.subscribe(newValue => {
        });
      }
    });

    // Monitor the entire form value changes
    this.dynamicForm.valueChanges.subscribe(formValues => {
    });
  }

  private getFieldsHash(fields: any): string {
    if (!fields?.SelectedFields) return '';
    return JSON.stringify(fields.SelectedFields.map((f: any) => ({
      FieldName: f.FieldName,
      Type: f.Type,
      Required: f.Required
    })));
  }

  private hasFieldsMeaningfullyChanged(newFields: any): boolean {
    const newHash = this.getFieldsHash(newFields);
    const hasChanged = newHash !== this.lastFieldsHash;
    this.lastFieldsHash = newHash;
    return hasChanged;
  }

  // TrackBy function to help Angular identify when components should be reused
  trackByField(index: number, field: any): string {
    if (!field || !field.FieldName) {
      return index.toString();
    }
    const id = this.valueInDispute && this.valueInDispute.id ? this.valueInDispute.id : 'noid';
    return `${field.FieldName}_${id}`;
  }

  // TrackBy function for file categories
  trackByFileCategory(index: number, category: any): string {
    if (!category || !category.id) {
      return index.toString();
    }
    const id = this.valueInDispute && this.valueInDispute.id ? this.valueInDispute.id : 'noid';
    return `${category.id}_${id}`;
  }

  // Get unique key for product selection to force re-rendering
  getProductSelectionKey(): string {
    const productId = this.getFormControlSafe('Product')?.value || 'none';
    const id = this.valueInDispute && this.valueInDispute.id ? this.valueInDispute.id : 'noid';
    return `${productId}_${id}`;
  }

  private removeExistingFieldControls(): void {
    // Remove all existing field controls for this dispute box
    if (this.selectedProductFields?.SelectedFields) {
      this.selectedProductFields.SelectedFields.forEach((field: any) => {
        const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
        if (this.dynamicForm.contains(controlName)) {
          this.dynamicForm.removeControl(controlName);
        }
      });
    }
  }

  private removeUnusedFieldControls(newFields: any[]): void {
    // Remove only controls that are no longer needed in the new product
    if (this.selectedProductFields?.SelectedFields) {
      const newFieldNames = new Set(newFields.map((field: any) => field.fieldName));

      this.selectedProductFields.SelectedFields.forEach((field: any) => {
        // Only remove if this field is not in the new product
        if (!newFieldNames.has(field.FieldName)) {
          const controlName = `${field.FieldName}_${this.valueInDispute.id}`;
          if (this.dynamicForm.contains(controlName)) {
            this.dynamicForm.removeControl(controlName);
            this.formControlCache.delete(controlName);
          }
        }
      });
    }
  }
}
